import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setCompany } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      setUser(data.user);
      // refresh company
      try {
        const me = await api.get("/auth/me");
        setCompany(me.data.company || null);
      } catch (err) {
        console.debug("company refresh failed", err);
      }
      toast.success("Bem-vindo de volta!");
      if (data.user.role === "super_admin") {
        navigate("/admin/metrics");
      } else if (data.user.role === "timeclock_kiosk") {
        navigate("/kiosk");
      } else {
        navigate(from);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao entrar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE EMERGENT GOOGLE AUTH FLOW.
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-stone-50">
      <div className="hidden lg:flex lg:w-1/2 bg-stone-950 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.4), transparent 40%), radial-gradient(circle at 80% 60%, rgba(5,150,105,0.3), transparent 50%)",
        }} />
        <div className="relative z-10 flex flex-col h-full justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Ultra" className="h-10 w-10 rounded-lg" />
            <div>
              <div className="font-display text-2xl font-extrabold">Ultra</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-emerald-300">Cozinha organizada</div>
            </div>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
              Operação rastreável.<br />Confiança operacional.
            </h2>
            <p className="mt-4 font-body text-stone-300 max-w-md">
              Acesse sua plataforma de gestão de cozinha industrial — checklists, APPCC, auditorias e ocorrências em um só lugar.
            </p>
          </div>
          <div className="text-xs text-stone-500 font-body">© Ultra</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 rounded-2xl border-stone-200">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="Ultra" className="h-8 w-8 rounded-md" />
            <span className="font-display text-lg font-extrabold">Ultra</span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Entrar</h1>
          <p className="font-body text-sm text-stone-500 mt-2">
            Acesse sua conta para continuar operando.
          </p>

          <form onSubmit={handleSubmit} data-testid={TID.loginForm} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="font-body text-sm">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                required
                data-testid={TID.loginEmail}
                className="mt-1.5 bg-white"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-body text-sm">Senha</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-body text-emerald-800 hover:underline"
                  data-testid="login-forgot-password"
                >
                  Esqueci minha senha
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid={TID.loginPassword}
                className="mt-1.5 bg-white"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid={TID.loginSubmit}
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white h-11 font-body"
            >
              {submitting ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="relative my-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs font-body uppercase tracking-[0.2em] text-stone-400">
              ou
            </span>
          </div>

          <Button
            type="button"
            onClick={handleGoogle}
            data-testid={TID.loginGoogle}
            variant="outline"
            className="w-full h-11 border-stone-300 bg-white hover:bg-stone-100 font-body"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com Google
          </Button>

          <p className="mt-6 text-center text-sm text-stone-500 font-body">
            Ainda não tem conta?{" "}
            <Link to="/register" className="text-emerald-800 font-semibold hover:underline" data-testid={TID.loginGoToRegister}>
              Cadastre sua empresa
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
